// src/services/notificationService.js
import { serverUrl } from '../temp';

const API_BASE_URL = serverUrl;

class NotificationService {
  constructor() {
    this.swRegistration = null;
  }

  // Helper to get token from localStorage
  getAuthHeaders() {
    const token = localStorage.getItem('token');
    return token ? { Authorization: `Bearer ${token}` } : {};
  }

  // Check if browser supports notifications
  isSupported() {
    return 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
  }

  // Initialize service worker
  async initializeServiceWorker() {
    if (!this.isSupported()) {
      throw new Error('Push notifications are not supported in this browser');
    }

    try {
      // Register service worker
      this.swRegistration = await navigator.serviceWorker.register('/sw.js');
      console.log('Service Worker registered successfully');
      return this.swRegistration;
    } catch (error) {
      console.error('Service Worker registration failed:', error);
      throw error;
    }
  }

  // Get notification permission
  async requestPermission() {
    const permission = await Notification.requestPermission();
    if (permission !== 'granted') {
      throw new Error('Notification permission denied');
    }
    return permission;
  }

  // Get VAPID public key from backend
  async getVapidPublicKey() {
    try {
      const response = await fetch(`${API_BASE_URL}/api/notifications/vapid-key`);
      const data = await response.json();
      return data.publicKey;
    } catch (error) {
      console.error('Failed to get VAPID key:', error);
      throw error;
    }
  }

  // Subscribe to push notifications
  async subscribe(userId) {
    try {
      // Ensure service worker is ready
      const registration = await navigator.serviceWorker.ready;

      // Get VAPID public key
      const vapidPublicKey = await this.getVapidPublicKey();
      const convertedVapidKey = this.urlBase64ToUint8Array(vapidPublicKey);

      // Subscribe to push notifications
      const subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: convertedVapidKey
      });

      // Get browser and device info
      const browser = navigator.userAgent;
      const device = navigator.platform || 'Unknown';

      // Send subscription to backend
      const response = await fetch(`${API_BASE_URL}/api/notifications/subscribe?user_id=${userId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...this.getAuthHeaders(),
        },
        body: JSON.stringify({
          subscription: subscription.toJSON(),
          browser,
          device
        })
      });

      if (!response.ok) {
        throw new Error('Failed to save subscription on server');
      }

      return await response.json();
    } catch (error) {
      console.error('Failed to subscribe:', error);
      throw error;
    }
  }

  // Unsubscribe from push notifications
  async unsubscribe(userId) {
    try {
      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.getSubscription();

      let endpoint = null;
      if (subscription) {
        endpoint = subscription.endpoint;
        await subscription.unsubscribe();
      }

      if (!endpoint) {
        throw new Error('No subscription found to unsubscribe');
      }

      // Notify backend
      const response = await fetch(`${API_BASE_URL}/api/notifications/unsubscribe?user_id=${userId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...this.getAuthHeaders(),
        },
        body: JSON.stringify({
          endpoint
        })
      });

      if (!response.ok) {
        throw new Error('Failed to unsubscribe on server');
      }

      return await response.json();
    } catch (error) {
      console.error('Failed to unsubscribe:', error);
      throw error;
    }
  }

  // Get notification status
  async getStatus(userId) {
    try {
      const response = await fetch(`${API_BASE_URL}/api/notifications/status?user_id=${userId}`, {
        headers: this.getAuthHeaders()
      });
      if (!response.ok) {
        throw new Error('Failed to get status');
      }
      return await response.json();
    } catch (error) {
      console.error('Failed to get status:', error);
      throw error;
    }
  }

  // Get notification settings
  async getSettings(userId) {
    try {
      const response = await fetch(`${API_BASE_URL}/api/notifications/settings?user_id=${userId}`, {
        headers: this.getAuthHeaders()
      });
      if (!response.ok) {
        throw new Error('Failed to get settings');
      }
      return await response.json();
    } catch (error) {
      console.error('Failed to get settings:', error);
      throw error;
    }
  }

  // Update notification settings
  async updateSettings(userId, centerId, settings) {
    try {
      const response = await fetch(`${API_BASE_URL}/api/notifications/settings?user_id=${userId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...this.getAuthHeaders(),
        },
        body: JSON.stringify({ center_id: centerId, ...settings })
      });
      if (!response.ok) {
        throw new Error('Failed to update settings');
      }
      return await response.json();
    } catch (error) {
      console.error('Failed to update settings:', error);
      throw error;
    }
  }

  // Send test notification
  async sendTestNotification(userId) {
    try {
      const response = await fetch(`${API_BASE_URL}/api/notifications/test?user_id=${userId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...this.getAuthHeaders(),
        },
        body: JSON.stringify({})
      });
      if (!response.ok) {
        throw new Error('Failed to send test notification');
      }
      return await response.json();
    } catch (error) {
      console.error('Failed to send test notification:', error);
      throw error;
    }
  }

  // Helper function to convert VAPID key
  urlBase64ToUint8Array(base64String) {
    const padding = '='.repeat((4 - base64String.length % 4) % 4);
    const base64 = (base64String + padding)
      .replace(/\-/g, '+')
      .replace(/_/g, '/');

    const rawData = window.atob(base64);
    const outputArray = new Uint8Array(rawData.length);

    for (let i = 0; i < rawData.length; ++i) {
      outputArray[i] = rawData.charCodeAt(i);
    }
    return outputArray;
  }
}

export default new NotificationService();